import ReactGA from 'react-ga4';

type PageView = {
  location: string;
  path: string;
  title: string;
};

type MapLinkClick = {
  service: string;
  url: string;
};

type EventParameters = Record<string, boolean | string>;

type ConversionOptions = {
  eventCallback?: () => void;
  eventTimeout?: number;
};

const readEnvironmentValue = (value: string | undefined) => value?.trim() || undefined;

const gaId = readEnvironmentValue(import.meta.env.VITE_GA_ID);
const googleAdsId = readEnvironmentValue(import.meta.env.VITE_GOOGLE_ADS_ID);
const phoneConversionLabel = readEnvironmentValue(
  import.meta.env.VITE_GOOGLE_ADS_PHONE_CONVERSION_LABEL
);
const accessConversionLabel = readEnvironmentValue(
  import.meta.env.VITE_GOOGLE_ADS_ACCESS_CONVERSION_LABEL
);

const getCurrentPagePath = () =>
  typeof window === 'undefined' ? '' : `${window.location.pathname}${window.location.search}`;

const sendGa4Event = (eventName: string, parameters: EventParameters = {}) => {
  if (!gaId || typeof window === 'undefined') return;

  ReactGA.gtag('event', eventName, {
    ...parameters,
    send_to: gaId,
  });
};

const sendGoogleAdsConversion = (
  conversionLabel: string | undefined,
  { eventCallback, eventTimeout }: ConversionOptions = {}
) => {
  if (!googleAdsId || !conversionLabel || typeof window === 'undefined') return false;

  ReactGA.gtag('event', 'conversion', {
    send_to: `${googleAdsId}/${conversionLabel}`,
    ...(eventCallback ? { event_callback: eventCallback } : {}),
    ...(eventTimeout ? { event_timeout: eventTimeout } : {}),
  });

  return true;
};

export const initializeGoogleTag = () => {
  if (typeof window === 'undefined') return;

  const trackingConfigurations: Array<{
    trackingId: string;
    gtagOptions: Record<string, boolean>;
  }> = [];

  if (gaId) {
    trackingConfigurations.push({
      trackingId: gaId,
      gtagOptions: { send_page_view: false },
    });
  }

  if (googleAdsId) {
    trackingConfigurations.push({
      trackingId: googleAdsId,
      gtagOptions: {
        allow_ad_personalization_signals: false,
        send_page_view: false,
      },
    });
  }

  if (trackingConfigurations.length > 0) {
    ReactGA.initialize(trackingConfigurations);
  }
};

export const trackPageView = ({ location, path, title }: PageView) => {
  sendGa4Event('page_view', {
    page_location: location,
    page_path: path,
    page_title: title,
  });
};

export const trackAccessPageView = () => {
  sendGoogleAdsConversion(accessConversionLabel);
};

export const trackPhoneClick = (url: string) => {
  sendGa4Event('phone_click', {
    link_url: url,
    page_path: getCurrentPagePath(),
  });

  let navigationStarted = false;
  const continueNavigation = () => {
    if (navigationStarted) return;

    navigationStarted = true;
    window.location.href = url;
  };
  const conversionSent = sendGoogleAdsConversion(phoneConversionLabel, {
    eventCallback: continueNavigation,
    eventTimeout: 1_000,
  });

  if (conversionSent) {
    window.setTimeout(continueNavigation, 1_000);
  }

  return conversionSent;
};

export const trackMapLinkClick = ({ service, url }: MapLinkClick) => {
  sendGa4Event('map_directions_click', {
    link_text: service,
    link_url: url,
    page_path: getCurrentPagePath(),
  });
};
